import { useState, useEffect, useCallback } from "react";

interface UseZoomClientProps {
  onClientReady?: (client: any) => void;
}

/**
 * Custom hook to initialize and manage Zoom client
 */
export const useZoomClient = ({ onClientReady }: UseZoomClientProps = {}) => {
  const [client, setClient] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Initialize the Zoom client
  useEffect(() => {
    const initializeClient = async () => {
      try {
        setIsInitializing(true);
        const ZoomVideo = window.WebVideoSDK.default;
        const zoomClient = ZoomVideo.createClient();
        await zoomClient.init("en-US", "CDN", {
          patchJsMedia: true,
          wasmSimd: false,
          webCodecs: false
        } as any);

        setClient(zoomClient);
        if (onClientReady) {
          onClientReady(zoomClient);
        }
      } catch (err) {
        console.error("Failed to initialize Zoom client:", err);
        setError(err as Error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeClient();
  }, [onClientReady]);

  // Clean up when component unmounts
  useEffect(() => {
    if (!client) return;

    const handleBeforeUnload = async () => {
      try {
        await client.leave();
      } catch (e) {
        // Ignore errors during cleanup
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [client]);

  return {
    client,
    isInitializing,
    error
  };
};

export default useZoomClient;
